import React, { useEffect, useState } from "react";
import { Pill } from "../order/style";
import {
  Alert,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  UncontrolledTooltip,
  Spinner,
} from "reactstrap";
import { updateOrderStatus } from "./service";
import ConfirmationModal from "../project/ConfirmationModal";
import { formatDateWithSuffix } from "../common/DateHelpers";

const OrderInfo = ({
  projectSummary,
  setProjectSummary,
  setCurrentTab,
  reloadRoom,
  isMobile,
  fromProjectCard,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [nextStatus, setNextStatus] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationContent, setConfirmationContent] = useState({
    heading: "",
    message: "",
    func: null,
  });
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paymentValue: "",
    paymentDate: "",
    otherReason: false,
    approveReason: "",
    mode: "cash",
    remarks: "",
  });
  const [orderPlacementData, setOrderPlacementData] = useState({
    contactNumber: "",
    siteLocation: "",
    siteMapUrl: "",
  });
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const statusTextMapper = {
    finalised: {
      onConfirm: "Request Validation",
      onSuccess: "Requesting Validation",
    },
    confirmed: {
      onConfirm: "Place Order",
      onSuccess: "Placing Order",
    },
  };

  const toggleModal = () => setShowModal(!showModal);

  const toggleConfirmation = () => setConfirmationOpen(!confirmationOpen);

  const togglePaymentModal = () => setPaymentModalOpen(!paymentModalOpen);

  useEffect(() => {
    if (!showModal) setNextStatus(null);
  }, [showModal]);

  const changeOrderStatus = (status, placementData = null) => {
    setIsStatusChanging(true);
    const isSupportProject = projectSummary.is_support === true;

    if (["finalised", "confirmed"].includes(status)) {
      // For support projects, skip confirm dialog and modal toggle
      const shouldProceed =
        isSupportProject ||
        window.confirm(
          `Are you sure you want to ${statusTextMapper[status].onConfirm}?`
        );

      if (shouldProceed) {
        if (!isSupportProject) {
          toggleModal();
        }

        const updatePayload = {
          projectId: projectSummary.id,
          orderId: projectSummary.order_id,
          status,
        };

        const dataToUse = placementData || orderPlacementData;
        if (status === "confirmed" && dataToUse) {
          updatePayload.contactNumber = dataToUse.contactNumber;
          updatePayload.siteLocation = dataToUse.siteLocation;
          updatePayload.siteMapUrl = dataToUse.siteMapUrl || "";
        }

        updateOrderStatus(updatePayload)
          .then((res) => {
            setProjectSummary(res);
            reloadRoom();
            setNextStatus(null);
            setIsStatusChanging(false);
          })
          .catch((error) => {
            setIsStatusChanging(false);
            console.error("Error updating order status:", error);
          });
      } else {
        setIsStatusChanging(false);
      }
    } else {
      updateOrderStatus({
        projectId: projectSummary.id,
        orderId: projectSummary.order_id,
        status,
      })
        .then((res) => {
          setProjectSummary(res);
          reloadRoom();
          setIsStatusChanging(false);
        })
        .catch((error) => {
          setIsStatusChanging(false);
          console.error("Error updating order status:", error);
        });
    }
  };

  const handlePaymentReceived = () => {
    const hasPayment = paymentData.paymentValue && paymentData.paymentDate;
    const hasApproveReason =
      paymentData.otherReason && paymentData.approveReason?.trim();

    if (hasPayment || hasApproveReason) {
      setIsStatusChanging(true);

      const updatePayload = {
        projectId: projectSummary.id,
        orderId: projectSummary.order_id,
        status: "placed",
      };

      if (paymentData.paymentValue)
        updatePayload.paymentValue = paymentData.paymentValue;

      if (paymentData.paymentDate)
        updatePayload.paymentDate = paymentData.paymentDate;

      if (paymentData.approveReason?.trim())
        updatePayload.approveReason = paymentData.approveReason.trim();

      updatePayload.mode = paymentData.mode;

      if (paymentData.remarks.trim())
        updatePayload.remarks = paymentData.remarks.trim();

      updateOrderStatus(updatePayload)
        .then((res) => {
          setProjectSummary(res);
          reloadRoom();
          togglePaymentModal();
          setPaymentData({
            paymentValue: "",
            paymentDate: "",
            otherReason: false,
            approveReason: "",
            mode: "cash",
            remarks: "",
          });
          setIsStatusChanging(false);
        })
        .catch((error) => {
          setIsStatusChanging(false);
          console.error("Error processing payment:", error);
        });
    }
  };

  return (
    <>
      {!isMobile && projectSummary.order_status && !fromProjectCard && (
        <Pill className="me-2 pointer" onClick={() => setCurrentTab("order")}>
          {projectSummary.order_status}
        </Pill>
      )}
      {(() => {
        const isSupportProject = projectSummary.is_support === true;

        // Filter out "Request Validation" button for support projects
        const filteredActionButtons = isSupportProject
          ? projectSummary.action_buttons.filter(
              (e) => e.new_status !== "finalised"
            )
          : projectSummary.action_buttons;

        const approve = filteredActionButtons.find(
          (e) => e.new_status === "confirmed"
        );
        const approveOrder = filteredActionButtons.find(
          (e) => e.new_status === "approved"
        );
        const paymentReceived = filteredActionButtons.find(
          (e) => e.new_status === "placed"
        );
        const backToDraft = filteredActionButtons.find(
          (e) => e.new_status === "draft"
        );
        const actionButton = filteredActionButtons[0];

        if (approve)
          return (
            <>
              <div className="d-flex align-items-center gap-2">
                {backToDraft && (
                  <Button
                    color="light"
                    outline
                    className="rounded-1 fw-bold d-flex align-items-center text-white"
                    onClick={(e) => {
                      if (fromProjectCard) {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                      setConfirmationContent({
                        heading: backToDraft.heading || "Back to Draft",
                        message:
                          backToDraft.message ||
                          "Are you sure you want to move this order back to draft?",
                        func: () => changeOrderStatus(backToDraft.new_status),
                      });
                      toggleConfirmation();
                    }}
                    disabled={backToDraft.disabled || isStatusChanging}
                  >
                    {isStatusChanging && <Spinner size="sm" className="me-2" />}
                    {isStatusChanging ? "Processing..." : backToDraft.text}
                  </Button>
                )}
                <div id="place_order_btn">
                  <Button
                    color="primary"
                    className="rounded-1 fw-bold d-flex align-items-center"
                    onClick={(e) => {
                      if (fromProjectCard) {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                      // For support projects, directly place order without modal
                      if (isSupportProject) {
                        if (
                          window.confirm(`Are you sure you want to ${approve.text}?`)
                        ) {
                          changeOrderStatus(approve.new_status, {
                            contactNumber: "",
                            siteLocation: "",
                            siteMapUrl: "",
                          });
                        }
                      } else {
                        toggleModal();
                      }
                    }}
                    disabled={
                      projectSummary.is_price_stale ||
                      projectSummary.has_any_module_errors ||
                      projectSummary.is_template_stale ||
                      projectSummary.is_delivery_date_stale ||
                      isStatusChanging
                    }
                  >
                    {isStatusChanging && <Spinner size="sm" className="me-2" />}
                    {isStatusChanging
                      ? "Processing..."
                      : projectSummary.action_button_text || approve.text}
                  </Button>
                </div>
              </div>
              {(projectSummary.is_price_stale ||
                projectSummary.is_delivery_date_stale) && (
                <UncontrolledTooltip
                  placement="bottom"
                  target="place_order_btn"
                >
                  Refresh Price and Date to place order
                </UncontrolledTooltip>
              )}
              <Modal
                backdrop="static"
                isOpen={showModal}
                toggle={toggleModal}
                size="lg"
              >
                <div
                  className="d-flex justify-content-between p-3 rounded-1 text-white fw-bold"
                  style={{ background: "#283039" }}
                >
                  <p className="m-0 fs-6">
                    {nextStatus?.new_status === "finalised"
                      ? "Validation"
                      : "Place Order"}
                  </p>
                  <div
                    onClick={toggleModal}
                    className="d-flex align-items-center pointer"
                  >
                    <i className="fa-solid fa-close fs-6" />
                  </div>
                </div>
                <ModalBody className="p-0">
                  {nextStatus ? (
                    nextStatus.new_status === "confirmed" ? (
                      <PlaceOrderModal
                        nextStatus={nextStatus}
                        setNextStatus={setNextStatus}
                        changeOrderStatus={changeOrderStatus}
                        currentStatus={projectSummary.actual_status}
                        deliveryDate={projectSummary.planned_delivery_date}
                        projectSummary={projectSummary}
                        isStatusChanging={isStatusChanging}
                        orderPlacementData={orderPlacementData}
                        setOrderPlacementData={setOrderPlacementData}
                      />
                    ) : nextStatus.new_status === "finalised" &&
                      !isSupportProject ? (
                      <>
                        <MoveToApprovalModal
                          nextStatus={nextStatus}
                          setNextStatus={setNextStatus}
                          changeOrderStatus={changeOrderStatus}
                          deliveryDate={projectSummary.planned_delivery_date}
                          isStatusChanging={isStatusChanging}
                        />
                      </>
                    ) : null
                  ) : projectSummary.actual_status === "draft" ? (
                    <>
                      <div className="p-3 pb-0">
                        <p>Would you like to opt for validation?</p>
                        <p>
                          A basic check will be done to flag any issues in your
                          design. This is optional and usually takes 2 or more
                          days, based on complexity and team availability.
                        </p>
                      </div>
                      <div
                        className="d-flex justify-content-end px-3 py-2"
                        style={{ background: "#EEEEEE" }}
                      >
                        <div className="d-flex flex-row-reverse column-gap-2">
                          {filteredActionButtons.map((e, index) => (
                            <Button
                              key={index}
                              color={index === 1 ? "secondary" : "primary"}
                              outline={index === 1 ? true : false}
                              className="fw-bold rounded-1 d-flex align-items-center"
                              onClick={() => setNextStatus(e)}
                              disabled={isStatusChanging}
                            >
                              {isStatusChanging && (
                                <Spinner size="sm" className="me-2" />
                              )}
                              {e.text}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <PlaceOrderModal
                      nextStatus={actionButton}
                      changeOrderStatus={changeOrderStatus}
                      currentStatus={projectSummary.actual_status}
                      toggleModal={toggleModal}
                      deliveryDate={projectSummary.planned_delivery_date}
                      projectSummary={projectSummary}
                      isStatusChanging={isStatusChanging}
                      orderPlacementData={orderPlacementData}
                      setOrderPlacementData={setOrderPlacementData}
                    />
                  )}
                </ModalBody>
              </Modal>
            </>
          );
        else if (approveOrder)
          return (
            <>
              <div className="d-flex align-items-center gap-2">
                {backToDraft && (
                  <Button
                    color="light"
                    outline
                    className="rounded-1 fw-bold d-flex align-items-center text-white"
                    onClick={(e) => {
                      if (fromProjectCard) {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                      setConfirmationContent({
                        heading: backToDraft.heading || "Back to Draft",
                        message:
                          backToDraft.message ||
                          "Are you sure you want to move this order back to draft?",
                        func: () => changeOrderStatus(backToDraft.new_status),
                      });
                      toggleConfirmation();
                    }}
                    disabled={backToDraft.disabled || isStatusChanging}
                  >
                    {isStatusChanging && <Spinner size="sm" className="me-2" />}
                    {isStatusChanging ? "Processing..." : backToDraft.text}
                  </Button>
                )}
                <Button
                  color="primary"
                  className="rounded-1 fw-bold d-flex align-items-center"
                  disabled={approveOrder.disabled || isStatusChanging}
                  onClick={(e) => {
                    if (fromProjectCard) {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                    setConfirmationContent({
                      heading: approveOrder.heading || "Approve Order",
                      message:
                        approveOrder.message ||
                        "Are you sure you want to approve the order?",
                      func: () => changeOrderStatus(approveOrder.new_status),
                    });
                    toggleConfirmation();
                  }}
                >
                  {isStatusChanging && <Spinner size="sm" className="me-2" />}
                  {isStatusChanging ? "Processing..." : approveOrder.text}
                </Button>
              </div>
            </>
          );
        else if (paymentReceived)
          return (
            <>
              {backToDraft && (
                <Button
                  color="light"
                  outline
                  className="rounded-1 fw-bold d-flex align-items-center me-2 text-white"
                  onClick={(e) => {
                    if (fromProjectCard) {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                    setConfirmationContent({
                      heading: backToDraft.heading || "Back to Draft",
                      message:
                        backToDraft.message ||
                        "Are you sure you want to move this order back to draft?",
                      func: () => changeOrderStatus(backToDraft.new_status),
                    });
                    toggleConfirmation();
                  }}
                  disabled={backToDraft.disabled || isStatusChanging}
                >
                  {isStatusChanging && <Spinner size="sm" className="me-2" />}
                  {isStatusChanging ? "Processing..." : backToDraft.text}
                </Button>
              )}
              <Button
                color="primary"
                className="rounded-1 fw-bold d-flex align-items-center"
                disabled={paymentReceived.disabled || isStatusChanging}
                onClick={(e) => {
                  if (fromProjectCard) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                  togglePaymentModal();
                }}
              >
                {isStatusChanging && <Spinner size="sm" className="me-2" />}
                {isStatusChanging ? "Processing..." : paymentReceived.text}
              </Button>
            </>
          );
        else if (projectSummary.action_buttons.length === 1)
          return (
            <Button
              color="primary"
              className="rounded-1 fw-bold d-flex align-items-center"
              disabled={actionButton.disabled || isStatusChanging}
              onClick={(e) => {
                if (fromProjectCard) {
                  e.stopPropagation();
                  e.preventDefault();
                }
                setConfirmationContent({
                  heading: actionButton.heading,
                  message: actionButton.message,
                  func: () => changeOrderStatus(actionButton.new_status),
                });
                toggleConfirmation();
              }}
            >
              {isStatusChanging && <Spinner size="sm" className="me-2" />}
              {isStatusChanging ? "Processing..." : actionButton.text}
            </Button>
          );
      })()}
      <ConfirmationModal
        isOpen={confirmationOpen}
        toggle={toggleConfirmation}
        onConfirm={confirmationContent["func"]}
        message={confirmationContent["message"]}
        headingText={confirmationContent["heading"]}
      />
      <PaymentModal
        isOpen={paymentModalOpen}
        toggle={togglePaymentModal}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        onConfirm={handlePaymentReceived}
        isStatusChanging={isStatusChanging}
      />
    </>
  );
};

const MoveToApprovalModal = ({
  nextStatus,
  setNextStatus,
  changeOrderStatus,
  deliveryDate,
  isStatusChanging,
}) => {
  const [checkbox, setCheckbox] = useState(false);

  return (
    <>
      <div className="p-3">
        <h6 className="mb-3">Validation Terms</h6>
        <ol className="d-flex flex-column row-gap-2 ps-3">
          <li>
            Your design can be sent for basic validation, which usually takes
            around 2 working days. Based on this, your tentative delivery date
            is <b>{formatDateWithSuffix(deliveryDate)}</b>, and the final date
            will be shared once the order is confirmed.
          </li>
          <li>
            During validation, we'll do a broad check covering key elements like
            cabinet structure, exposed sides, and layout. While we'll flag
            anything that clearly stands out, you're responsible for checking
            measurements, cabinet list, and final selections.
          </li>
          <li>
            We'll do our best to catch any issues, but since the design is added
            by you, some details may go unnoticed. Final responsibility stays
            with you.
          </li>
          <li>
            In some cases, validation may take longer depending on the design
            complexity or team availability.
          </li>
          <li>
            You can choose to skip the validation and place your order anytime.
          </li>
        </ol>
        <div className="d-flex column-gap-2">
          <Input
            className="m-0 border-secondary fs-6 rounded-1"
            type="checkbox"
            id="approval-confirmation"
            checked={checkbox}
            onChange={(e) => setCheckbox(e.target.checked)}
            disabled={isStatusChanging}
          />
          <Label className="m-0 fw-bold" for="approval-confirmation">
            I agree to all the terms.
          </Label>
        </div>
      </div>
      <div
        className="d-flex justify-content-end px-3 py-2"
        style={{ background: "#EEEEEE" }}
      >
        <div className="d-flex column-gap-2">
          <Button
            outline
            className="fw-bold rounded-1"
            onClick={() => setNextStatus(null)}
            disabled={isStatusChanging}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="fw-bold rounded-1 d-flex align-items-center"
            onClick={() => changeOrderStatus(nextStatus.new_status)}
            disabled={!checkbox || isStatusChanging}
          >
            {isStatusChanging && <Spinner size="sm" className="me-2" />}
            {isStatusChanging ? "Processing..." : nextStatus.text}
          </Button>
        </div>
      </div>
    </>
  );
};

const PlaceOrderModal = ({
  nextStatus,
  setNextStatus,
  changeOrderStatus,
  currentStatus,
  toggleModal,
  deliveryDate,
  projectSummary,
  isStatusChanging,
  orderPlacementData,
  setOrderPlacementData,
}) => {
  const isSupportProject = projectSummary.is_support === true;

  const [checkboxes, setCheckboxes] = useState({
    delivery: false,
    scope: false,
    payment: false,
    unloading: false,
    support: false,
  });

  const [localPlacementData, setLocalPlacementData] = useState(() => {
    return {
      contactNumber: orderPlacementData?.contactNumber || "",
      siteLocation: orderPlacementData?.siteLocation || "",
      siteMapUrl: orderPlacementData?.siteMapUrl || "",
    };
  });

  const handleCheckboxChange = (key) => {
    setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // For support projects, skip validation - all checkboxes considered checked
  const allChecked =
    isSupportProject || Object.values(checkboxes).every(Boolean);

  // For support projects, skip contact info validation
  const isContactInfoValid =
    isSupportProject ||
    (localPlacementData.contactNumber?.trim() &&
      localPlacementData.siteLocation?.trim() &&
      localPlacementData.siteMapUrl?.trim());

  return (
    <>
      <div className="p-3">
        {nextStatus.disabled ? (
          <Alert color="warning" className="text-black m-0">
            <span className="fw-bold">
              A few laminates in your project are still awaiting approval.{" "}
            </span>
            <br />
            <span>
              Please contact the Boxs team or check back later to place the
              order.
            </span>
          </Alert>
        ) : (
          <>
            {!isSupportProject && (
              <>
                <div>
                  <h6>Delivery Timeline</h6>
                  <p>
                    Your estimated delivery date is{" "}
                    <b>
                      {projectSummary.delivery_date_from &&
                      projectSummary.delivery_date_to
                        ? `${formatDateWithSuffix(
                            projectSummary.delivery_date_from
                          )} - ${formatDateWithSuffix(
                            projectSummary.delivery_date_to
                          )}`
                        : formatDateWithSuffix(deliveryDate)}
                    </b>
                    . While we aim to deliver on time, minor delays may happen
                    due to material shortages, weather, or equipment issues.
                    You'll be kept informed of any changes.
                  </p>
                  <InputGroup
                    id="delivery-confirmation"
                    checked={checkboxes.delivery}
                    handleCheckboxChange={() =>
                      handleCheckboxChange("delivery")
                    }
                    text="I understand the delivery date is estimated and may shift if
                needed."
                    disabled={isStatusChanging}
                  />
                </div>
                <hr></hr>
                <div>
                  <h6>Scope & Accuracy</h6>
                  <p>
                    Products will be made exactly as per the details entered in
                    the Boxs app. Even if validation is used, the final output
                    will follow your inputs. You are responsible for reviewing
                    and confirming accuracy.
                  </p>
                  <InputGroup
                    id="scope-confirmation"
                    checked={checkboxes.scope}
                    handleCheckboxChange={() => handleCheckboxChange("scope")}
                    text="I confirm the app inputs are correct and final."
                    disabled={isStatusChanging}
                  />
                </div>
                <hr></hr>
                <div>
                  <h6>Payments</h6>
                  <p>
                    A 50% advance is needed to begin processing. The balance is
                    due before dispatch. For orders below ₹25,000, full payment
                    is required before dispatch - no advance needed. Credit
                    terms will apply if agreed.
                  </p>
                  <InputGroup
                    id="payment-confirmation"
                    checked={checkboxes.payment}
                    handleCheckboxChange={() => handleCheckboxChange("payment")}
                    text="I agree to the payment terms shared above."
                    disabled={isStatusChanging}
                  />
                </div>
                <hr></hr>
                <div>
                  <h6>Transportation & Unloading</h6>
                  <p>
                    Products will be delivered by third-party partners, and
                    charges are paid directly to them. Unloading isn't included
                    by Boxs, but can be coordinated with the delivery partner if
                    needed.
                  </p>
                  <InputGroup
                    id="unloading-confirmation"
                    checked={checkboxes.unloading}
                    handleCheckboxChange={() =>
                      handleCheckboxChange("unloading")
                    }
                    text="I understand delivery and unloading are handled separately, and I'll coordinate if required."
                    disabled={isStatusChanging}
                  />
                </div>
                <hr></hr>
                <div>
                  <h6>Post-Delivery Support</h6>
                  <p>
                    Support will be provided for site-related issues as per our
                    standard terms. Ensuring the accuracy of inputs in the app
                    is your responsibility.
                  </p>
                  <InputGroup
                    id="support-confirmation"
                    checked={checkboxes.support}
                    handleCheckboxChange={() => handleCheckboxChange("support")}
                    text="I accept responsibility for the inputs and agree to the support terms."
                    disabled={isStatusChanging}
                  />
                </div>
                <hr></hr>
                <div>
                  <h6>Contact & Site Information</h6>
                  <div className="d-flex gap-3 align-items-start">
                    <div className="flex-grow-1">
                      <Label for="site-location" className="fw-bold">
                        Site Location <span className="text-danger">*</span>
                      </Label>
                      <textarea
                        id="site-location"
                        rows="3"
                        placeholder="Enter site location"
                        value={localPlacementData.siteLocation}
                        onChange={(e) =>
                          setLocalPlacementData((prev) => ({
                            ...prev,
                            siteLocation: e.target.value,
                          }))
                        }
                        className={`form-control rounded-1 ${
                          !localPlacementData.siteLocation.trim()
                            ? "border-danger"
                            : ""
                        }`}
                        style={{ minHeight: "120px" }}
                      />
                      {!localPlacementData.siteLocation.trim() && (
                        <small className="text-danger">
                          Site location is required
                        </small>
                      )}
                    </div>

                    <div
                      className="d-flex flex-column"
                      style={{ minWidth: "250px" }}
                    >
                      <div className="mb-3">
                        <Label for="contact-number" className="fw-bold">
                          Contact Number <span className="text-danger">*</span>
                        </Label>
                        <input
                          id="contact-number"
                          type="text"
                          placeholder="Enter contact number"
                          value={localPlacementData.contactNumber}
                          onChange={(e) =>
                            setLocalPlacementData((prev) => ({
                              ...prev,
                              contactNumber: e.target.value,
                            }))
                          }
                          className={`form-control rounded-1 ${
                            !localPlacementData.contactNumber.trim()
                              ? "border-danger"
                              : ""
                          }`}
                        />
                        {!localPlacementData.contactNumber.trim() && (
                          <small className="text-danger">
                            Contact number is required
                          </small>
                        )}
                      </div>

                      <div className="mb-3">
                        <Label for="site-map-url" className="fw-bold">
                          Site Map URL <span className="text-danger">*</span>
                        </Label>
                        <input
                          id="site-map-url"
                          type="text"
                          placeholder="Enter site map URL"
                          value={localPlacementData.siteMapUrl}
                          onChange={(e) =>
                            setLocalPlacementData((prev) => ({
                              ...prev,
                              siteMapUrl: e.target.value,
                            }))
                          }
                          className="form-control rounded-1"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {isSupportProject && (
              <div className="p-3">
                <p className="mb-0">Placing order for support project...</p>
              </div>
            )}
            {currentStatus !== "approved" && (
              <Alert color="warning" className="text-black mt-3">
                <span className="fw-bold">
                  {currentStatus === "finalised"
                    ? "Validation is currently in progress."
                    : "Please note that you're placing the order directly and have chosen not to use our validation service."}
                </span>
                <br />
                {currentStatus === "finalised" && (
                  <span>
                    If you choose to place the order now, the ongoing review
                    will be cancelled and your design will be processed as it
                    is.
                  </span>
                )}
              </Alert>
            )}
          </>
        )}
      </div>
      <div
        className="d-flex justify-content-end px-3 py-2"
        style={{ background: "#EEEEEE" }}
      >
        <div className="d-flex column-gap-2">
          <Button
            outline
            className="fw-bold rounded-1"
            onClick={() =>
              setNextStatus ? setNextStatus(null) : toggleModal()
            }
            disabled={isStatusChanging}
          >
            Cancel
          </Button>
          {!nextStatus.disabled && (
            <Button
              color="primary"
              className="fw-bold rounded-1 d-flex align-items-center"
              onClick={() => {
                // For support projects, use empty placement data
                const placementDataToUse = isSupportProject
                  ? { contactNumber: "", siteLocation: "", siteMapUrl: "" }
                  : localPlacementData;

                if (setOrderPlacementData) {
                  setOrderPlacementData(placementDataToUse);
                }
                changeOrderStatus(nextStatus.new_status, placementDataToUse);
              }}
              disabled={!allChecked || !isContactInfoValid || isStatusChanging}
            >
              {isStatusChanging && <Spinner size="sm" className="me-2" />}
              {isStatusChanging
                ? "Processing..."
                : currentStatus === "finalised"
                ? "Skip Validation & Place Order"
                : nextStatus.text}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

const InputGroup = ({ id, checked, handleCheckboxChange, text, disabled }) => {
  return (
    <div className="d-flex column-gap-2">
      <Input
        className="m-0 border-secondary fs-6 rounded-1"
        type="checkbox"
        id={id}
        checked={checked}
        onChange={handleCheckboxChange}
        disabled={disabled}
      />
      <Label className="m-0 fw-bold" for={id}>
        {text}
      </Label>
    </div>
  );
};

const PaymentModal = ({
  isOpen,
  toggle,
  paymentData,
  setPaymentData,
  onConfirm,
  isStatusChanging,
}) => {
  const handleInputChange = (field, value) => {
    setPaymentData((prev) => ({ ...prev, [field]: value }));
  };

  const hasPayment = paymentData.paymentValue && paymentData.paymentDate;
  const hasApproveReason =
    paymentData.otherReason && paymentData.approveReason?.trim();
  const isFormValid = hasPayment || hasApproveReason;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <div
        className="d-flex justify-content-between p-3 rounded-1 text-white fw-bold"
        style={{ background: "#283039" }}
      >
        <p className="m-0 fs-6">Payment Received</p>
        <div onClick={toggle} className="d-flex align-items-center pointer">
          <i className="fa-solid fa-close fs-6" />
        </div>
      </div>
      <ModalBody className="p-3">
        <div className="mb-3">
          <Label for="payment-value" className="fw-bold">
            Payment Value (₹)
          </Label>
          <Input
            id="payment-value"
            type="number"
            placeholder="Enter payment amount"
            value={paymentData.paymentValue}
            onChange={(e) => handleInputChange("paymentValue", e.target.value)}
            className="rounded-1"
            disabled={isStatusChanging}
          />
        </div>
        <div className="mb-3">
          <Label for="payment-date" className="fw-bold">
            Payment Date
          </Label>
          <Input
            id="payment-date"
            type="date"
            value={paymentData.paymentDate}
            onChange={(e) => handleInputChange("paymentDate", e.target.value)}
            className="rounded-1"
            disabled={isStatusChanging}
          />
        </div>
        <div className="mb-3">
          <Label for="mode">Payment Mode *</Label>
          <Input
            id="mode"
            type="select"
            name="mode"
            value={paymentData.mode}
            onChange={(e) => handleInputChange("mode", e.target.value)}
            className="rounded-1"
            disabled={isStatusChanging}
          >
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="credit_note">Credit Note</option>
          </Input>
        </div>
        <div className="mb-3">
          <Label for="remarks">Remarks</Label>
          <Input
            id="remarks"
            type="textarea"
            name="remarks"
            value={paymentData.remarks}
            onChange={(e) => handleInputChange("remarks", e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="mb-3">
          <div className="d-flex column-gap-2 align-items-center">
            <Input
              type="checkbox"
              id="other-reason"
              checked={paymentData.otherReason}
              onChange={(e) => {
                const isChecked = e.target.checked;
                handleInputChange("otherReason", isChecked);
                // Reset approve reason when checkbox is unchecked
                if (!isChecked) {
                  handleInputChange("approveReason", "");
                }
              }}
              disabled={isStatusChanging}
              className="m-0 border-secondary fs-6 rounded-1"
            />
            <Label for="other-reason" className="m-0 fw-bold">
              Other Reason
            </Label>
          </div>
        </div>
        {paymentData.otherReason && (
          <div className="mb-3">
            <Label for="approve-reason" className="fw-bold">
              Approve Reason <span className="text-danger">*</span>
            </Label>
            <Input
              id="approve-reason"
              type="text"
              placeholder="Enter approve reason"
              value={paymentData.approveReason}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 250) {
                  handleInputChange("approveReason", value);
                }
              }}
              className={`rounded-1 ${
                paymentData.otherReason && !paymentData.approveReason?.trim()
                  ? "border-danger"
                  : ""
              }`}
              disabled={isStatusChanging}
              maxLength={250}
            />
            {paymentData.otherReason && !paymentData.approveReason?.trim() && (
              <small className="text-danger">Approve reason is required</small>
            )}
          </div>
        )}
        <div className="d-flex justify-content-end column-gap-2">
          <Button
            outline
            className="fw-bold rounded-1"
            onClick={toggle}
            disabled={isStatusChanging}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="fw-bold rounded-1 d-flex align-items-center"
            onClick={onConfirm}
            disabled={!isFormValid || isStatusChanging}
          >
            {isStatusChanging && <Spinner size="sm" className="me-2" />}
            {isStatusChanging ? "Processing..." : "Confirm Payment"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default OrderInfo;
